/**
 * useAppBadge — single source of truth for the OS app icon badge.
 *
 * Drives `navigator.setAppBadge(n)` / `navigator.clearAppBadge()` from a
 * single place so we never get fighting writers or duplicated polling.
 * Also broadcasts the count to the active service worker so the badge
 * stays correct after a push wakes the SW with stale data.
 *
 * Triggers that refresh the count:
 *   • mount (after auth)
 *   • window focus
 *   • document visibility change (tab returns to foreground)
 *   • 60s poll while the tab is visible
 *   • the global `pie:badge-invalidate` CustomEvent
 *     (fired by code paths that mutate read state — mark-as-read,
 *     mark-all-read, opening a chat conversation, etc.)
 *
 * Platform support: gracefully no-ops on Firefox / browser tab Safari.
 * Works on iOS 16.4+ Home-Screen PWA, macOS Safari 16+ PWA,
 * Chrome/Edge installed PWA on Win/Mac/Linux/ChromeOS, Android Chrome PWA.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/utils';

const BADGE_INVALIDATE_EVENT = 'pie:badge-invalidate';

export function invalidateAppBadge() {
  try {
    window.dispatchEvent(new CustomEvent(BADGE_INVALIDATE_EVENT));
  } catch (_) {
    /* ignore */
  }
}

async function applyBadge(n) {
  const count = Math.max(0, Number(n) || 0);
  // Foreground (page) — works for installed PWA windows on every platform
  // that supports the Web Badging API.
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (_) {
      /* unsupported / not installed — ignore */
    }
  }
  // Service worker (background) — keeps the badge correct when the page is
  // closed and a push wakes the SW. The SW also writes the badge directly
  // from the push payload, but mirroring keeps everything in lockstep.
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    reg?.active?.postMessage({ type: 'SET_BADGE', count });
  } catch (_) {
    /* ignore */
  }
}

export default function useAppBadge() {
  const { user } = useAuth();
  const pollRef = useRef(null);
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const data = await apiRequest('/notifications/badge');
      const total = Number(data?.total) || 0;
      await applyBadge(total);
    } catch (_) {
      /* swallow — badge is best effort */
    } finally {
      inFlightRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      // Logged out — clear the badge so a former user's count doesn't stick.
      applyBadge(0);
      return;
    }

    // Initial read
    refresh();

    // 60s poll — only ticks while the tab is visible to save battery / API.
    const startPoll = () => {
      stopPoll();
      pollRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') refresh();
      }, 60_000);
    };
    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    startPoll();

    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onInvalidate = () => refresh();

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(BADGE_INVALIDATE_EVENT, onInvalidate);

    return () => {
      stopPoll();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(BADGE_INVALIDATE_EVENT, onInvalidate);
    };
  }, [user, refresh]);

  return { refresh };
}
